import {
  IconDefinition,
  faCalculator,
  faCalendar,
  faCartShopping,
  faChartNetwork,
  faCreditCard,
  faHandshake,
  faIdCard,
  faInboxIn,
  faInboxOut,
  faMoneyBill,
  faMoneyBillTransfer,
  faMoneyCheckDollarPen,
  faMoneyFromBracket,
  faPhone,
  faPiggyBank,
  faRobot,
} from '@fortawesome/pro-regular-svg-icons';
import {
  CreditTransactionType,
  PayoutCategory,
  PayoutType,
  Type,
} from '@enrollhere/classes';
import { IconListItemA } from '../models';

export const creditTransactionTypeMap: {
  [key: number]: {
    title: string;
    description: string;
    icon: IconDefinition;
  };
} = {
  [CreditTransactionType.CTT_CREDIT]: {
    title: 'Credit',
    description: 'Credit transaction',
    icon: faMoneyBill,
  },
  [CreditTransactionType.CTT_CARRY_FORWARD]: {
    title: 'Carry Forward',
    description: 'Carry forward transaction',
    icon: faCalculator,
  },
  [CreditTransactionType.CTT_TOP_UP]: {
    title: 'Top Up',
    description: 'Top up transaction',
    icon: faPiggyBank,
  },
  [CreditTransactionType.CTT_USAGE]: {
    title: 'Usage',
    description: 'Usage transaction',
    icon: faMoneyCheckDollarPen,
  },
  [CreditTransactionType.CTT_TRANSFER_IN]: {
    title: 'Transfer In',
    description: 'Transfer of funds into an account',
    icon: faInboxIn,
  },
  [CreditTransactionType.CTT_TRANSFER_OUT]: {
    title: 'Transfer Out',
    description: 'Transfer of funds out of an account',
    icon: faInboxOut,
  },
  [CreditTransactionType.CTT_LOCK_RELEASE]: {
    title: 'Lock Release',
    description: 'Lock release transaction',
    icon: faHandshake,
  },
  [CreditTransactionType.CTT_ALLOWANCE_SPEND]: {
    title: 'Allowance Spend',
    description: 'Spending from agent allowance',
    icon: faMoneyCheckDollarPen,
  },
  [CreditTransactionType.CTT_REFUND]: {
    title: 'Refund',
    description: 'Refund transaction',
    icon: faMoneyBill,
  },
  [CreditTransactionType.CTT_FEE]: {
    title: 'Fee',
    description: 'Fee transaction',
    icon: faMoneyCheckDollarPen,
  },
  [CreditTransactionType.CTT_COMMISSION]: {
    title: 'Commission',
    description: 'Commission transaction',
    icon: faCalculator,
  },
  [CreditTransactionType.CTT_FUND_BLOCK_SPEND]: {
    title: 'Fund Block Spend',
    description: 'Spending from specific fund block',
    icon: faMoneyCheckDollarPen,
  },
  [CreditTransactionType.CTT_FUND_BLOCK_CREATE]: {
    title: 'Fund Block Create',
    description: 'Creating a new fund block',
    icon: faPiggyBank,
  },
  [CreditTransactionType.CTT_FUND_BLOCK_RELEASE]: {
    title: 'Fund Block Release',
    description: 'Releasing unused fund block',
    icon: faHandshake,
  },
};

export const payoutTypeMap: {
  [key: number]: {
    title: string;
    subTitle?: string;
    description?: string;
    icon: IconDefinition;
    category: string;
  };
} = {
  [PayoutType.PT_AUTO_CALCULATED]: {
    title: 'Auto Calculated',
    description: 'Commissions that are calculated automatically.',
    icon: faCalculator,
    category: 'PT_AUTO_CALCULATED',
  },
  [PayoutType.PT_SEND_PAYOUT]: {
    title: 'Send Payout',
    subTitle: 'No Repayment',
    description:
      'Money given as commission, bonus, or compensation.	No repayment required; no impact on debt or balance.',
    icon: faHandshake,
    category: 'PT_SEND',
  },
  [PayoutType.PT_SEND_LOAN_ADVANCE]: {
    title: 'Loan / Advance',
    subTitle: 'Repayment Expected',
    description:
      'Money given as a loan or advance. Creates or increases a negative balance (debt) for the agent.',
    icon: faMoneyCheckDollarPen,
    category: 'PT_SEND',
  },
  [PayoutType.PT_RECEIVE_LOAN_REPAYMENT]: {
    title: 'Loan Repayment',
    subTitle: 'Specific Loan',
    description:
      'The agent repays a specific loan or debt. You manually select the account being repaid.',
    icon: faPiggyBank,
    category: 'PT_RECEIVE',
  },
  [PayoutType.PT_RECEIVE_GENERAL_PAYMENT]: {
    title: 'Receive Payment',
    subTitle: 'Not Assigned',
    description:
      'Money received from the agent without specifying the purpose upfront. The system holds this as a temporary balance and processes it during the cycle.',
    icon: faMoneyBill,
    category: 'PT_RECEIVE',
  },
  [PayoutType.PT_USAGE]: {
    title: 'Usage',
    description: 'Usage of a resource',
    icon: faMoneyFromBracket,
    category: 'PT_USAGE',
  },
  [PayoutType.PT_TOP_UP]: {
    title: 'Add Funds',
    description: 'Add funds to a resource',
    icon: faCreditCard,
    category: 'PT_TOP_UP',
  },
  [PayoutType.PT_AUTO_TOP_UP]: {
    title: 'Auto Add Funds',
    description: 'Auto add funds to a resource',
    icon: faCreditCard, // Use same icon as PT_TOP_UP for consistency
    category: 'PT_AUTO_TOP_UP',
  },
  [PayoutType.PT_TRANSFER]: {
    title: 'Transfer',
    description: 'Transfer of funds between accounts',
    icon: faMoneyBillTransfer,
    category: 'PT_TRANSFER',
  },
};

export const payoutTypeCategoryMap = {
  PT_SEND: {
    title: 'Send',
    icon: faInboxOut,
  },
  PT_RECEIVE: {
    title: 'Receive',
    icon: faInboxIn,
  },
};

export const payoutTypeCategoryList = [
  {
    ...payoutTypeCategoryMap['PT_SEND'],
    id: 'PT_SEND',
  },
  {
    ...payoutTypeCategoryMap['PT_RECEIVE'],
    id: 'PT_RECEIVE',
  },
];

const payoutCategoryMap = {
  [PayoutCategory.PC_COMMISSION]: {
    title: 'Commission',
    icon: faMoneyBill,
  },
  [PayoutCategory.PC_ADDITIONAL_COMPENSATION]: {
    title: 'Additional Compensation',
    icon: faMoneyBill,
  },
};

export const payoutCategoryList = [
  {
    ...payoutCategoryMap[PayoutCategory.PC_COMMISSION],
    id: PayoutCategory.PC_COMMISSION,
  },
  {
    ...payoutCategoryMap[PayoutCategory.PC_ADDITIONAL_COMPENSATION],
    id: PayoutCategory.PC_ADDITIONAL_COMPENSATION,
  },
];

export const typeMap: {
  [key: number]: Partial<IconListItemA>;
} = {
  [Type.T_DIALER_CALL]: {
    title: 'Dialer Call',
    icon: faPhone,
  },
  [Type.T_APPOINTMENT]: {
    title: 'Appointment',
    icon: faCalendar,
  },
  [Type.T_LEAD_DATA_PURCHASE]: {
    title: 'Lead Data Purchase',
    icon: faIdCard,
  },
  [Type.T_LEAD_DATA_REQUEST]: {
    title: 'Lead Data Request',
    icon: faCartShopping,
  },
  [Type.T_SIGNAL_360]: {
    title: 'EH Signal 360',
    icon: faChartNetwork,
  },
};
